import {Component, ElementRef, input, output, signal, viewChild, model, effect, OnDestroy, ChangeDetectionStrategy} from "@angular/core"
import {clamp} from "@app/legacy/helpers/utils"
import {Vector2Like} from "@cm/math"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-spline-curve",
    templateUrl: "./spline-curve.component.html",
    styleUrl: "./spline-curve.component.scss",
})
export class SplineCurveComponent implements OnDestroy {
    readonly $curveContainer = viewChild.required<ElementRef<HTMLElement>>("curveContainer")
    readonly $curvePathsGroup = viewChild.required<ElementRef<SVGElement>>("curvePathsGroup")
    readonly $otherPathsGroup = viewChild.required<ElementRef<SVGElement>>("otherPathsGroup")

    readonly $controlPoints = input.required<Vector2Like[]>({alias: "controlPoints"})
    readonly $curvePoints = input.required<{position: Vector2Like; color?: string}[]>({alias: "curvePoints"})
    readonly $otherPathsPoints = input<{position: Vector2Like; color?: string}[][]>([], {alias: "otherPathsPoints"})
    readonly $selectedControlPoint = model<number | undefined>(undefined, {alias: "selectedControlPoint"})
    readonly $hoveredControlPoint = model<number | undefined>(undefined, {alias: "hoveredControlPoint"})
    readonly $width = input(100, {alias: "width"})
    readonly $height = input(100, {alias: "height"})
    readonly $isDown = signal(false)

    readonly onSplineClicked = output<Vector2Like>()
    readonly onBackgroundClicked = output<Vector2Like>()
    readonly onBeginControlPointDrag = output<void>()
    readonly onControlPointChanged = output<{pointId: number; point: Vector2Like}>()
    readonly onEndControlPointDrag = output<void>()
    readonly onBackgroundMouseDown = output<void>()
    readonly onBackgroundMouseUp = output<void>()
    readonly onMouseMoved = output<Vector2Like>()
    readonly onWheelMoved = output<{deltaX: number; deltaY: number}>()

    constructor() {
        effect(() => {
            this.updateCurvePaths()
        })

        effect(() => {
            this.updateOtherPaths()
        })
    }

    ngOnDestroy(): void {
        this._removeGlobalListeners()
    }

    private mouseEventToCoordinates = (event: MouseEvent) => {
        const container = this.$curveContainer().nativeElement
        const rect = container.getBoundingClientRect()
        const relX = event.clientX - rect.left
        const relY = event.clientY - rect.top
        return {x: relX / rect.width, y: 1 - relY / rect.height}
    }

    private _globalMouseMove = (event: MouseEvent) => this.onMouseMove(event)
    private _globalMouseUp = (event: MouseEvent) => this.onMouseUp(event)

    private _isListening = false

    onSplineClick(event: MouseEvent) {
        this.onSplineClicked.emit(this.mouseEventToCoordinates(event))
    }

    onBackgroundClick(event: MouseEvent) {
        const {x, y} = this.mouseEventToCoordinates(event)
        if (x < 0 || x > 1 || y < 0 || y > 1) return
        this.onBackgroundClicked.emit({x, y})
    }

    onMouseDown(event: MouseEvent, pointId?: number): void {
        if (pointId === undefined) {
            this.onBackgroundMouseDown.emit()
            this.$selectedControlPoint.set(undefined)
        } else {
            this.onBeginControlPointDrag.emit()
            this.$selectedControlPoint.set(pointId)
        }

        this.$isDown.set(true)

        if (!this._isListening) {
            document.addEventListener("mousemove", this._globalMouseMove)
            document.addEventListener("mouseup", this._globalMouseUp)
            this._isListening = true
        }
    }

    onMouseUp(_event: MouseEvent) {
        this.$isDown.set(false)

        if (this.$selectedControlPoint() === undefined) {
            this.onBackgroundMouseUp.emit()
        } else {
            this.onEndControlPointDrag.emit()
        }
        this._removeGlobalListeners()
    }

    private _removeGlobalListeners() {
        if (this._isListening) {
            document.removeEventListener("mousemove", this._globalMouseMove)
            document.removeEventListener("mouseup", this._globalMouseUp)
            this._isListening = false
        }
    }

    onMouseMove(event: MouseEvent) {
        if (!this.$isDown()) return

        event.preventDefault()
        event.stopPropagation()

        const {x, y} = this.mouseEventToCoordinates(event)
        const newX = clamp(x, 0, 1)
        const newY = clamp(y, 0, 1)

        const selectedPoint = this.$selectedControlPoint()

        if (selectedPoint === undefined) {
            this.onMouseMoved.emit({x: newX, y: newY})
        } else {
            this.onControlPointChanged.emit({pointId: selectedPoint, point: {x: newX, y: newY}})
        }
    }

    onMouseEnterControlPoint(pointId: number) {
        this.$hoveredControlPoint.set(pointId)
    }

    onMouseLeaveControlPoint() {
        this.$hoveredControlPoint.set(undefined)
    }

    onWheelEvent(event: WheelEvent) {
        event.preventDefault()
        event.stopPropagation()

        this.onWheelMoved.emit(event)
    }

    private updateCurvePaths() {
        const curvePoints = this.$curvePoints()
        const curvePathsGroup = this.$curvePathsGroup().nativeElement

        // Clear existing paths
        while (curvePathsGroup.firstChild) {
            curvePathsGroup.removeChild(curvePathsGroup.firstChild)
        }

        // Group curve points by color
        const coloredCurvePointsSets: {curvePoints: Vector2Like[]; color: string}[] = []
        for (let i = 0; i < curvePoints.length; i++) {
            const curvePointPosition = curvePoints[i].position
            const curvePointColor = curvePoints[i].color ?? "black"
            if (coloredCurvePointsSets.length === 0) {
                coloredCurvePointsSets.push({curvePoints: [curvePointPosition], color: curvePointColor})
                continue
            }

            const currentSet = coloredCurvePointsSets[coloredCurvePointsSets.length - 1]
            const currentSetColor = currentSet.color
            if (currentSetColor === curvePointColor) {
                currentSet.curvePoints.push(curvePointPosition)
            } else {
                const lastCurvePoint = currentSet.curvePoints[currentSet.curvePoints.length - 1]
                coloredCurvePointsSets.push({
                    curvePoints: [lastCurvePoint, curvePointPosition],
                    color: curvePointColor,
                })
            }
        }

        const width = this.$width()
        const height = this.$height()

        const pathForCurvePoints = (curvePoints: Vector2Like[]) => {
            let path = ""

            curvePoints.forEach((point) => {
                const screnX = point.x * width
                const screenY = (1 - point.y) * height
                if (path === "") path = `M ${screnX} ${screenY}`
                else path += ` L ${screnX} ${screenY}`
            })

            return path
        }

        // Create new paths
        coloredCurvePointsSets.forEach(({curvePoints, color}) => {
            const mainPath = document.createElementNS("http://www.w3.org/2000/svg", "path")
            mainPath.setAttribute("d", pathForCurvePoints(curvePoints))
            mainPath.setAttribute("clip-path", "url(#clipRgb)")
            mainPath.setAttribute("stroke", color)
            mainPath.setAttribute("stroke-width", "1")
            mainPath.setAttribute("fill", "none")
            curvePathsGroup.appendChild(mainPath)

            const transparentPath = document.createElementNS("http://www.w3.org/2000/svg", "path")
            transparentPath.setAttribute("d", pathForCurvePoints(curvePoints))
            transparentPath.setAttribute("clip-path", "url(#clipRgb)")
            transparentPath.setAttribute("stroke", "transparent")
            transparentPath.setAttribute("stroke-width", "4")
            transparentPath.setAttribute("fill", "none")
            transparentPath.setAttribute("pointer-events", "stroke")
            transparentPath.addEventListener("click", (event) => {
                this.onSplineClick(event as MouseEvent)
                event.stopPropagation()
            })
            curvePathsGroup.appendChild(transparentPath)
        })
    }

    private updateOtherPaths() {
        const otherPathsPoints = this.$otherPathsPoints()
        const otherPathsGroup = this.$otherPathsGroup().nativeElement

        // Clear existing paths
        while (otherPathsGroup.firstChild) {
            otherPathsGroup.removeChild(otherPathsGroup.firstChild)
        }

        const pathForOtherPathsPoints = (otherPathPoints: {position: Vector2Like; color?: string; stroke?: string}[]) => {
            // Group curve points by color
            const coloredOtherPathPointsSets: {curvePoints: Vector2Like[]; color: string}[] = []
            for (let i = 0; i < otherPathPoints.length; i++) {
                const curvePointPosition = otherPathPoints[i].position
                const curvePointColor = otherPathPoints[i].color ?? "black"
                if (coloredOtherPathPointsSets.length === 0) {
                    coloredOtherPathPointsSets.push({curvePoints: [curvePointPosition], color: curvePointColor})
                    continue
                }

                const currentSet = coloredOtherPathPointsSets[coloredOtherPathPointsSets.length - 1]
                const currentSetColor = currentSet.color
                if (currentSetColor === curvePointColor) {
                    currentSet.curvePoints.push(curvePointPosition)
                } else {
                    const lastCurvePoint = currentSet.curvePoints[currentSet.curvePoints.length - 1]
                    coloredOtherPathPointsSets.push({
                        curvePoints: [lastCurvePoint, curvePointPosition],
                        color: curvePointColor,
                    })
                }
            }

            const width = this.$width()
            const height = this.$height()

            const pathForCurvePoints = (curvePoints: Vector2Like[]) => {
                let path = ""

                curvePoints.forEach((point) => {
                    const screnX = point.x * width
                    const screenY = (1 - point.y) * height
                    if (path === "") path = `M ${screnX} ${screenY}`
                    else path += ` L ${screnX} ${screenY}`
                })

                return path
            }

            // Create new paths
            coloredOtherPathPointsSets.forEach(({curvePoints, color}) => {
                const mainPath = document.createElementNS("http://www.w3.org/2000/svg", "path")
                mainPath.setAttribute("d", pathForCurvePoints(curvePoints))
                mainPath.setAttribute("clip-path", "url(#clipRgb)")
                mainPath.setAttribute("stroke", color)
                mainPath.setAttribute("stroke-width", "1")
                mainPath.setAttribute("fill", "none")
                otherPathsGroup.appendChild(mainPath)
            })
        }

        otherPathsPoints.forEach((otherPathPoints) => {
            pathForOtherPathsPoints(otherPathPoints)
        })
    }
}
